// ProfileAnalyticsCard — owner-agent-only view of profile reach.
//
// Server gate: this component only renders when the API response includes
// profile_views_summary (the endpoint omits the field entirely for non-
// owner viewers). PlayerProfile.tsx ALSO checks viewer agent id ===
// player.agent.id before mounting, as a belt-and-suspenders defense.
// Either check alone is enough; the doubled check makes it impossible
// for a frontend bug to leak agent metrics to non-owners.
//
// Layout matches the wingspan mock:
//   1. Header strip — section title + Lock icon + reassurance copy.
//   2. Dark navy trend banner — direction-aware (up/down) + zero-divide
//      safe ("New this period" when prev14d is 0).
//   3. Three stat tiles — Profile views (total), Unique clubs, Film plays.
//   4. Recent viewers table — capped at 5; anonymized rows already
//      filtered server-side.

import { Lock, TrendingDown, TrendingUp } from 'lucide-react'

export interface ProfileViewsSummary {
  total: number
  last14d_count: number
  prev14d_count: number
  delta_pct: number | null
  unique_clubs: number
  film_plays: number
  recent_viewers: Array<{
    viewer_role: string
    viewer_club_name: string | null
    viewer_country: string | null
    viewed_at: string
  }>
}

function ago(iso: string): string {
  // SQLite default datetime is local-ish ('YYYY-MM-DD HH:MM:SS') with no
  // timezone — Date.parse interprets it in the JS env's local zone, which
  // is good enough for an "X ago" label.
  const t = Date.parse(iso.replace(' ', 'T'))
  if (Number.isNaN(t)) return iso
  const diff = Date.now() - t
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.floor(hr / 24)
  if (days === 1) return '1d ago'
  if (days < 7) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks}w ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  const years = Math.floor(days / 365)
  return `${years}y ago`
}

export default function ProfileAnalyticsCard({
  summary,
}: {
  summary: ProfileViewsSummary
}) {
  const delta = summary.delta_pct
  const direction: 'up' | 'down' | 'new' =
    delta == null ? 'new' : delta >= 0 ? 'up' : 'down'

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl overflow-hidden">
      {/* Header strip with reassurance copy */}
      <header className="px-5 py-3 border-b border-hoop-border bg-hoop-bg">
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-hoop-text-2" />
          <h2 className="text-[12px] font-bold uppercase tracking-wider text-hoop-accent">
            Profile Analytics
          </h2>
        </div>
        <p className="text-[11px] text-hoop-text-3 mt-1">
          Visible to agent only — coaches and players cannot see this data.
        </p>
      </header>

      {/* Trend banner */}
      <div className="px-5 py-3 bg-hoop-nav text-hoop-nav-fg">
        <div className="flex items-center gap-2 text-sm">
          {direction === 'down' ? (
            <TrendingDown className="w-4 h-4 text-hoop-red" />
          ) : (
            <TrendingUp className="w-4 h-4 text-hoop-green" />
          )}
          {direction === 'new' && (
            <span>
              <span className="font-semibold text-hoop-green">New this period</span>
              {' · '}
              {summary.last14d_count} {summary.last14d_count === 1 ? 'view' : 'views'} in the last 14 days.
            </span>
          )}
          {direction === 'up' && (
            <span>
              Profile views{' '}
              <span className="font-semibold text-hoop-green">
                up {delta == null ? '0' : delta}% in the last 14 days.
              </span>
            </span>
          )}
          {direction === 'down' && (
            <span>
              Profile views{' '}
              <span className="font-semibold text-hoop-red">
                down {delta == null ? '0' : Math.abs(delta)}% in the last 14 days.
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 divide-x divide-hoop-border border-b border-hoop-border">
        <StatTile label="Profile views" value={summary.total} />
        <StatTile label="Unique clubs" value={summary.unique_clubs} />
        <StatTile label="Film plays" value={summary.film_plays} />
      </div>

      {/* Recent viewers */}
      <div className="p-5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-hoop-accent mb-2">
          Recent viewers
        </h3>
        {summary.recent_viewers.length === 0 ? (
          <p className="text-xs text-hoop-text-3">
            No viewers yet.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-hoop-text-3 border-b border-hoop-border">
                  <th className="py-2 px-2 font-medium">Country</th>
                  <th className="py-2 px-2 font-medium">Club</th>
                  <th className="py-2 px-2 font-medium">Role</th>
                  <th className="py-2 px-2 font-medium text-right">Viewed</th>
                </tr>
              </thead>
              <tbody>
                {summary.recent_viewers.map((v, i) => (
                  <tr
                    key={`${v.viewed_at}-${i}`}
                    className="border-b border-hoop-border last:border-b-0"
                  >
                    <td className="py-2 px-2 text-hoop-text">
                      {v.viewer_country ?? '—'}
                    </td>
                    <td className="py-2 px-2 text-hoop-text">
                      {v.viewer_club_name ?? '—'}
                    </td>
                    <td className="py-2 px-2 text-hoop-text-2 capitalize">
                      {v.viewer_role}
                    </td>
                    <td className="py-2 px-2 text-right text-hoop-text-3 whitespace-nowrap">
                      {ago(v.viewed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-4 text-center">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-hoop-accent mb-1">
        {label}
      </div>
      <div className="text-2xl font-bold text-hoop-text tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  )
}
